import { useCallback, useEffect, useMemo } from 'react';
import type { QuickFillItem } from '../../../shared/types';
import type { CellUpdateResult } from '../../../shared/services/quickFillRevitService';
import { useHostRibbonChromeTargets } from '../../../shared/ui/common/hostRibbonChromeTargets';
import { perf } from '../../../shared/lib/perfLogger';
import { useSnackbarStore } from '../../../shared/stores/useSnackbarStore';
import { useQuickFillDialogs } from '.';
import { hasSeenQuickFillOnboarding } from '../lib/quickFillOnboarding';
import {
  buildQuickFillTourSteps,
} from '../ui/quickFillGuideContent';
import { quickFillChromeControllers } from '../ui/quickFillChromeControllers';
import {
  pickPreferredQuickFillLabel,
  PREVIEW_SAVE_REVIEW_RESULT,
  PREVIEW_SAVE_REVIEW_WARNING,
  resolveQuickFillTourNavigationState,
} from '../ui/quickFillTourState';
import type { EmbeddedQuickFillActions } from '../ui/quickFillPageTypes';
import type { QuickFillPageInteractionController } from './useQuickFillPageInteractionController';

const PROPERTY_PANEL_TOUR_STEP_IDS = new Set(['columns', 'column-panel-search']);

export function useQuickFillPageExperienceController(
  controller: QuickFillPageInteractionController,
  embedded: boolean,
  registerEmbeddedActions?: (actions: EmbeddedQuickFillActions | null) => void
) {
  const {
    validationFocus, manualRowModeOverrideRef, setRowMode, connectorDefaultRowMode,
    shouldUseFamilyTypeFallback, initialization, setViewportWidth, viewportWidth,
    quickFillData, visiblePropertyLabels, refs, tourFooterActionsRef, operations,
    updatePropertyRevitParameter, combinedError, cellSelection, navigation,
    useDrawerPanels, propertyPanelOpen, setPropertyPanelOpen, navigationState,
    filteredRowPositionMap, hiddenMarkerCount, submit, activeFindReplaceMatch,
    clearFindReplaceFocus,
  } = controller;
  const hostRibbonAvailable = typeof window !== 'undefined' && Boolean(window.chrome?.webview);
  const quickFillTourSteps = useMemo(() => buildQuickFillTourSteps({
    rowCount: quickFillData.length,
    hostRibbonAvailable,
    refs: {
      actionBarRef: refs.actionBarRef,
      pageGuideRef: refs.pageGuideRef,
      recoveryControlsRef: refs.recoveryControlsRef,
      navigationDockRef: refs.navigationDockRef,
      filterTriggerRef: refs.filterTriggerRef,
      filterPopoverRef: refs.filterPopoverRef,
      autoFillControlsRef: refs.autoFillControlsRef,
      manageColumnsRef: refs.manageColumnsRef,
      findReplaceRef: refs.findReplaceRef,
      rowModeToggleRef: refs.rowModeToggleRef,
      modelSelectRef: refs.modelSelectRef,
      csvToolsRef: refs.csvToolsRef,
      utilityToolsRef: refs.utilityToolsRef,
      tableViewportRef: refs.tableViewportRef,
      propertyPanelRef: refs.propertyPanelRef,
      propertySearchRef: refs.propertySearchRef,
      valuePanelRef: refs.valuePanelRef,
      valueSearchRef: refs.valueSearchRef,
      autoFillPreviewDialogRef: refs.autoFillPreviewDialogRef,
      saveReviewDialogRef: refs.saveReviewDialogRef,
      findReplaceDialogRef: refs.findReplaceDialogRef,
      footerActionsRef: tourFooterActionsRef,
    },
  }), [hostRibbonAvailable, quickFillData.length, refs, tourFooterActionsRef]);
  const tour = useQuickFillDialogs({
    handleAutoFillPreview: operations.handleAutoFillPreview,
    handleConfirmAutoFill: operations.handleConfirmAutoFill,
    updatePropertyRevitParameter,
    tourSteps: quickFillTourSteps,
    onBeforeOpenTour: useCallback(() => {
      refs.propertyPanelOpenBeforeTourRef.current = propertyPanelOpen;
    }, [propertyPanelOpen, refs.propertyPanelOpenBeforeTourRef]),
  });
  const handleOpenTour = tour.handleOpenTour;
  const setTransientSuppressed = useSnackbarStore((state) => state.setTransientSuppressed);

  useEffect(() => {
    setTransientSuppressed(tour.tourOpen);
    return () => setTransientSuppressed(false);
  }, [setTransientSuppressed, tour.tourOpen]);

  useEffect(() => {
    if (!hasSeenQuickFillOnboarding() && !combinedError) handleOpenTour();
  }, [combinedError, handleOpenTour]);
  useEffect(() => {
    if (validationFocus.effectiveValidationScope?.requestId || manualRowModeOverrideRef.current) {
      return;
    }
    setRowMode((current) => current === connectorDefaultRowMode
      ? current
      : connectorDefaultRowMode);
  }, [
    connectorDefaultRowMode, manualRowModeOverrideRef, setRowMode,
    validationFocus.effectiveValidationScope?.requestId,
  ]);
  useEffect(() => {
    if (!shouldUseFamilyTypeFallback) return;
    perf.warn('QF-Handoff', 'Using family type rows because no instance payload is available', {
      requestId: validationFocus.effectiveValidationScope?.requestId || null,
      labels: validationFocus.effectiveValidationScope?.labels || [],
      familyRowsAvailable: initialization.pipeFamilies.length,
    });
    setRowMode('familyType');
  }, [
    initialization.pipeFamilies.length, setRowMode, shouldUseFamilyTypeFallback,
    validationFocus.effectiveValidationScope?.labels,
    validationFocus.effectiveValidationScope?.requestId,
  ]);
  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setViewportWidth]);

  const fallbackChromeTargets = useMemo(
    () => hostRibbonAvailable ? quickFillChromeControllers(viewportWidth) : undefined,
    [hostRibbonAvailable, viewportWidth]
  );
  const quickFillHostChromeTargets = useHostRibbonChromeTargets(fallbackChromeTargets);
  const quickFillHostRibbonActions = useMemo(() => hostRibbonAvailable ? {
    onOpenGuide: tour.openGuide,
    onStartTour: tour.handleRestartTour,
  } : undefined, [hostRibbonAvailable, tour.handleRestartTour, tour.openGuide]);
  const currentTourStepId = tour.currentTourStepId;
  const showTourNavigatorStep = Boolean(tour.runtimeIntent.forceNavigatorPreview);
  const showTourFilterPreview = currentTourStepId === 'advanced-filters';
  const showTourSaveReviewPreview = Boolean(tour.runtimeIntent.showSaveReviewPreview);
  const previewValuePanelActive = Boolean(tour.runtimeIntent.openValuePanel) &&
    (!cellSelection.selectedCells.length || !cellSelection.isValidSelection || !cellSelection.activeColumn);
  const previewFilterColumn = useMemo(
    () => pickPreferredQuickFillLabel(visiblePropertyLabels, ['SKEY', 'OBJECT-TYPE']),
    [visiblePropertyLabels]
  );
  const previewValuePanelColumn = useMemo(
    () => pickPreferredQuickFillLabel(
      visiblePropertyLabels,
      ['SKEY', 'OBJECT-TYPE', 'ITEM-DESCRIPTION']
    ),
    [visiblePropertyLabels]
  );

  useEffect(() => {
    if (!tour.tourOpen && !showTourFilterPreview) return;
    perf.log('QF-WizardFilter', 'wizard filter state', {
      tourOpen: tour.tourOpen,
      tourStep: tour.tourStep,
      currentTourStepId,
      showTourFilterPreview,
      previewFilterColumn,
      visiblePropertyLabels: visiblePropertyLabels.slice(0, 6),
      filteredRows: navigation.filteredRowIndices.length,
      viewportStart: navigation.viewportRange.startPosition,
      viewportEnd: navigation.viewportRange.endPosition,
    });
  }, [
    currentTourStepId, navigation.filteredRowIndices.length,
    navigation.viewportRange.endPosition, navigation.viewportRange.startPosition,
    previewFilterColumn, showTourFilterPreview, tour.tourOpen, tour.tourStep,
    visiblePropertyLabels,
  ]);

  const valuePanelColumn = previewValuePanelActive
    ? previewValuePanelColumn
    : cellSelection.activeColumn;
  const valuePanelSelectedCount = previewValuePanelActive
    ? Math.min(Math.max(quickFillData.length, 3), 8)
    : cellSelection.selectedCells.length;
  const valuePanelOptionRowIndices = useMemo(() => {
    if (!valuePanelColumn || previewValuePanelActive) return [];
    return Array.from(new Set(cellSelection.selectedCells.filter(
      (cell) => cell.columnLabel === valuePanelColumn
    ).map((cell) => cell.rowIndex).filter(
      (index) => Number.isInteger(index) && index >= 0
    ))).sort((left, right) => left - right);
  }, [cellSelection.selectedCells, previewValuePanelActive, valuePanelColumn]);
  const valuePanelCurrentValue = valuePanelColumn && quickFillData.length > 0
    ? quickFillData[0]?.[valuePanelColumn as keyof QuickFillItem] as string | undefined
    : undefined;
  const shouldShowValuePanel = Boolean(valuePanelColumn) &&
    !propertyPanelOpen &&
    ((cellSelection.selectedCells.length > 0 &&
      cellSelection.isValidSelection &&
      Boolean(cellSelection.activeColumn)) || previewValuePanelActive);
  const shouldForceOpenNavigator = showTourNavigatorStep && !useDrawerPanels;
  const navigationPreviewState = useMemo(() => resolveQuickFillTourNavigationState({
    shouldForceOpenNavigator,
    navigationDockCollapsed: navigation.navigationDockCollapsed,
    activeNavigationRowIndex: navigationState.activeNavigationRowIndex,
    quickFillData,
    filteredRowIndices: navigation.filteredRowIndices,
    filteredRowPositionMap,
    markerItems: navigation.markerItems,
    hiddenMarkerCount,
    activeNavigationRowLabel: navigationState.activeNavigationRowLabel,
    activeRowMarked: navigationState.activeRowMarked,
    canJumpToActive: navigationState.canJumpToActive,
    canToggleMarker: navigationState.canToggleMarker,
    currentNavigationPosition: navigationState.currentNavigationPosition,
  }), [
    filteredRowPositionMap, hiddenMarkerCount, navigation.filteredRowIndices,
    navigation.markerItems, navigation.navigationDockCollapsed, navigationState,
    quickFillData, shouldForceOpenNavigator,
  ]);
  const handleJumpToActiveForDock = useCallback(() => {
    if (navigationPreviewState.isPreviewState) {
      if (navigationPreviewState.previewNavigationPosition != null) {
        navigation.handleJumpToPosition(navigationPreviewState.previewNavigationPosition);
      }
    } else {
      navigation.handleJumpToActiveLocation();
    }
  }, [navigation, navigationPreviewState]);
  const effectiveSaveReviewResult = showTourSaveReviewPreview
    ? PREVIEW_SAVE_REVIEW_RESULT
    : submit.saveResultDialog.result;
  const effectiveSaveReviewWarning = showTourSaveReviewPreview
    ? PREVIEW_SAVE_REVIEW_WARNING
    : submit.saveResultDialog.warning;
  useEffect(() => {
    if (!tour.effectiveFindReplaceOpen && activeFindReplaceMatch) clearFindReplaceFocus();
  }, [activeFindReplaceMatch, clearFindReplaceFocus, tour.effectiveFindReplaceOpen]);
  const successRows = useMemo<CellUpdateResult[]>(
    () => (effectiveSaveReviewResult?.results || []).filter((result) => result.success),
    [effectiveSaveReviewResult]
  );
  const failedRows = useMemo<CellUpdateResult[]>(
    () => (effectiveSaveReviewResult?.results || []).filter((result) => !result.success),
    [effectiveSaveReviewResult]
  );

  useEffect(() => {
    if (!tour.tourOpen) return;
    const step = quickFillTourSteps[tour.tourStep];
    const expose = Boolean(step?.id && PROPERTY_PANEL_TOUR_STEP_IDS.has(step.id));
    if (expose && !propertyPanelOpen) {
      refs.propertyPanelManagedByTourRef.current = true;
      setPropertyPanelOpen(true);
    } else if (
      !expose && refs.propertyPanelManagedByTourRef.current &&
      !refs.propertyPanelOpenBeforeTourRef.current && propertyPanelOpen
    ) {
      setPropertyPanelOpen(false);
      refs.propertyPanelManagedByTourRef.current = false;
    }
  }, [propertyPanelOpen, quickFillTourSteps, refs, setPropertyPanelOpen, tour.tourOpen, tour.tourStep]);
  useEffect(() => {
    if (!embedded || !registerEmbeddedActions) return;
    registerEmbeddedActions({
      submitChanges: submit.submitQuickFillChanges,
      openGuide: tour.openGuide,
      openTour: tour.handleOpenTour,
    });
    return () => registerEmbeddedActions(null);
  }, [embedded, registerEmbeddedActions, submit.submitQuickFillChanges, tour.handleOpenTour, tour.openGuide]);

  return {
    ...controller,
    hostRibbonAvailable,
    quickFillTourSteps,
    tour,
    quickFillHostChromeTargets,
    quickFillHostRibbonActions,
    showTourFilterPreview,
    showTourSaveReviewPreview,
    previewValuePanelActive,
    previewFilterColumn,
    valuePanelColumn,
    valuePanelSelectedCount,
    valuePanelOptionRowIndices,
    valuePanelCurrentValue,
    shouldShowValuePanel,
    showInlineTourValuePanel: useDrawerPanels && shouldShowValuePanel,
    showInlineTourPropertyPanel: useDrawerPanels && propertyPanelOpen,
    shouldForceOpenNavigator,
    navigationPreviewState,
    handleJumpToActiveForDock,
    handleToggleMarkerForDock: navigationPreviewState.isPreviewState
      ? () => undefined
      : navigation.handleToggleActiveMarker,
    handleRemoveMarkerForDock: navigationPreviewState.isPreviewState
      ? () => undefined
      : navigation.handleRemoveMarker,
    effectiveSaveReviewResult,
    effectiveSaveReviewWarning,
    effectiveSaveReviewOpen: submit.saveResultDialog.open || showTourSaveReviewPreview,
    saveReviewPreviewOnly: showTourSaveReviewPreview,
    successRows,
    failedRows,
  };
}

export type QuickFillPageExperienceController = ReturnType<
  typeof useQuickFillPageExperienceController
>;
